// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Shift from '@ta/overlays.shift';
import classNames from '@ta/common.classnames';
import type { ViewportCategory } from '@ta/common.state';
import Scrollable, { type SetChild, type SnapToEdge } from './Scrollable';
import ScrollArrow from './ScrollArrow';
import {
  directionObject,
  defaultPosition,
  type DirectionValue,
  type OptionalDirectionObject,
  type DirectionObject,
  type Position,
} from './util';

import styles from './ScrollAffordance.less';

/** Renders a container that will prompt scrolling arrows on directions it can scroll to.
 *
 *
 * ---------------------------------
 * Gotcha's to using this component
 * ---------------------------------
 * 1. The rendered scrolling container will be position: relative.
 * 2. Containers of this component must be position: relative.
 * 3. The setChild ref can be set on any descendent, not just its immediate children. However, any elements in between must not have position: relative set.
 * -----------------------------------------------------------------
 *
 * */
type ArrowPlacement = OptionalDirectionObject<boolean | React.Node>;

type RequiredArrowShift = DirectionObject<Position>;
type ArrowShift = ($Shape<Position> | number) | $Shape<DirectionObject<$Shape<Position> | number>>;
type Threshold = DirectionValue<number>;

/**
 * ScrollAffordance needs to own the top-level container for internal purposes, but we still want consumers to be able to customize it
 * Feel free to add any attributes if needed. (Only exception is that this component cannot be forward ref'd)
 *
 *
 * */
type ElementAttributes = {|
  className?: string,
  style?: any, // TODO: Find the proper type for React CSS Style
  onScroll?: (SyntheticMouseEvent<*>) => mixed,
  onClick?: (SyntheticMouseEvent<*>) => mixed,
|};

type ExternalProps = {|
  ...ArrowPlacement,
  ...ElementAttributes,
  /** Leeway before showing the arrows  */
  threshold?: Threshold,
  /** If a child is sufficiently close to an edge, then it scrolls to that edge instead. See documentation in Scrollable. */
  snapToEdge?: SnapToEdge,
  /** Pixel-by-pixel shift of the arrows. Note the shift is in the direction of the arrow */
  shift?: ArrowShift,
  /** The "anchor" of the scroll. See documentation in Scrollable. */
  focus?: Position,
  /** Scroll will skip children that are too close. See documentation in Scrollable. */
  minDistance?: number,
  /** What device types the scroll arrow will be visible for */
  viewportCategories?: ViewportCategory[],
  /** whether this is active (this can be on a hidden tab, so we'll know when it's turning active), default to true */
  isActive?: boolean,
  children: (setChild: SetChild) => React.Node,
|};

type Props = {|
  ...ExternalProps,
  viewportCategory: ViewportCategory,
  isRtl: boolean,
|};

type State = DirectionObject<boolean>;

type ValidatorProps = {|
  ...ArrowPlacement,
  ...ElementAttributes,
  threshold: Threshold,
  container: ?HTMLElement,
  onDirectionChange: (DirectionObject<boolean>) => void,
  isActive: boolean,
  children: React.Node,
|};

type ForwardedValidatorProps = {|
  ...ValidatorProps,
  forwardedRef: React.ElementRef<*>,
|};

// An incomplete shift is an object with potentially optional types for some of its fields
// This function default-initializes an arrow shift to 0, if needed
export const arrowShift = (shift: ArrowShift): RequiredArrowShift => {
  const { top, left, bottom, right } = directionObject(shift);
  const validTop = top == null ? defaultPosition : { ...defaultPosition, ...top };
  const validLeft = left == null ? defaultPosition : { ...defaultPosition, ...left };
  const validRight = right == null ? defaultPosition : { ...defaultPosition, ...right };
  const validBottom = bottom == null ? defaultPosition : { ...defaultPosition, ...bottom };
  return { top: validTop, left: validLeft, right: validRight, bottom: validBottom };
};

/** A render prop component which tells you which direction can be scrolled. */
class BoundsValidator extends React.Component<ForwardedValidatorProps> {
  static defaultProps = { left: true, bottom: true, right: true, top: true };
  static initialState = { top: false, left: false, right: false, bottom: false };

  // If the container ever changes, then always fire a callback
  componentDidUpdate({ container, isActive }: ForwardedValidatorProps) {
    if (
      this.props.isActive &&
      (!this.props.container ||
        (!this.props.container.clientWidth && !this.props.container.clientHeight))
    ) {
      // sometimes, componentDidUpdate gets invoked while the given container
      // still has no size (not rendered yet?!).
      // The problem is better explained here: https://www.pluralsight.com/tech-blog/react-size-position
      // Solution: poll until size is available and only then recompute arrow directions
      const containerSizePoller = setInterval(() => {
        if (!this.props.isActive) {
          // not active anymore, no need to keep polling
          clearInterval(containerSizePoller);
        }

        if (
          this.props.container &&
          this.props.container.clientWidth &&
          this.props.container.clientHeight
        ) {
          clearInterval(containerSizePoller);
          this.directions = this.getDirections();
          this.props.onDirectionChange(this.directions);
        }
      }, 100);
    } else {
      const newDirections = this.getDirections();

      if (
        container !== this.props.container ||
        (this.props.isActive && !isActive) ||
        this.didChange(newDirections)
      ) {
        this.directions = newDirections;
        this.props.onDirectionChange(this.directions);
      }
    }
  }

  getDirections = () => {
    const { top, left, right, bottom, container, threshold } = this.props;
    const {
      left: leftThreshold,
      right: rightThreshold,
      top: topThreshold,
      bottom: bottomThreshold,
    } = directionObject(threshold);
    const directions = { ...BoundsValidator.initialState };
    if (container) {
      const {
        clientWidth,
        clientHeight,
        scrollLeft,
        scrollTop,
        scrollWidth,
        scrollHeight,
      } = container;
      if (scrollLeft > leftThreshold && left) {
        directions.left = true;
      }
      if (scrollTop > topThreshold && top) {
        directions.top = true;
      }
      if (clientWidth + scrollLeft + rightThreshold < scrollWidth && right) {
        directions.right = true;
      }
      if (clientHeight + scrollTop + bottomThreshold < scrollHeight && bottom) {
        directions.bottom = true;
      }
      return directions;
    }
    return directions;
  };

  // Deep object equality
  didChange = ({ top: newTop, left: newLeft, right: newRight, bottom: newBottom }) => {
    const { top, left, right, bottom } = this.directions;
    return top !== newTop || left !== newLeft || right !== newRight || bottom !== newBottom;
  };

  checkArrows = () => {
    const newDirections = this.getDirections();
    if (this.didChange(newDirections)) {
      this.directions = newDirections;
      this.props.onDirectionChange(this.directions);
    }
  };

  handleOnScroll = (event: SyntheticMouseEvent<*>) => {
    this.checkArrows();
    if (this.props.onScroll) {
      this.props.onScroll(event);
    }
  };

  directions: DirectionObject<boolean> = { ...BoundsValidator.initialState };

  render() {
    const { forwardedRef, children, onClick, className, style } = this.props;
    return (
      <div
        ref={forwardedRef}
        onScroll={this.handleOnScroll}
        onClick={onClick}
        className={className}
        style={{ ...style, position: 'relative !important' }}
      >
        {children}
      </div>
    );
  }
}

/* eslint-disable react/no-multi-comp, react/display-name */
const ForwardedBoundsValidator = React.forwardRef<ValidatorProps, BoundsValidator>((props, ref) => (
  <BoundsValidator {...props} forwardedRef={ref} />
));
/* eslint-enable react/no-multi-comp, react/display-name */

const scrollArgs = (top, left, minDistance, focus = { top: 0, left: 0 }, snapToEdge) => ({
  direction: {
    top,
    left,
  },
  focus,
  scalarProjection: true,
  behavior: 'smooth',
  minDistance,
  snapToEdge,
  type: 'scrollToNearestChild',
});

// eslint-disable-next-line react/no-multi-comp
class ScrollAffordance extends React.Component<Props, State> {
  static defaultProps = {
    left: true,
    bottom: true,
    right: true,
    top: true,
    threshold: 0,
    snapToEdge: 0,
    shift: undefined,
    focus: { top: 0, left: 0 },
    className: undefined,
    onClick: undefined,
    onScroll: undefined,
    style: undefined,
    isActive: true,
    viewportCategories: ['TABLET', 'DESKTOP'],
  };

  state = { top: false, right: false, left: false, bottom: false };

  onDirectionChange = (directions: DirectionObject<boolean>) => {
    this.setState(directions);
  };
  render() {
    const {
      children,
      minDistance,
      snapToEdge,
      focus,
      shift,
      isRtl,
      viewportCategory,
      viewportCategories,
      isActive,
      ...elements
    } = this.props;
    const {
      top: enableTopArrow,
      left: enableLeftArrow,
      right: enableRightArrow,
      bottom: enableBottomArrow,
    } = this.props;
    const { top: topShift, left: leftShift, right: rightShift, bottom: bottomShift } = arrowShift(
      shift || 0,
    );
    return (
      <Scrollable>
        {({ scroll, setContainer, setChild, container }) => {
          const { top, left, right, bottom } = this.state;
          const arrows = [];
          if (container && viewportCategories && viewportCategories.includes(viewportCategory)) {
            const transitionNames = {
              appear: styles.appear,
              appearActive: styles.appear_active,
              enter: styles.enter,
              enterActive: styles.enter_active,
              exit: styles.exit,
              exitActive: styles.exit_active,
            };
            if (left && enableLeftArrow) {
              const onClick = () => {
                scroll(scrollArgs(0, -1, minDistance, focus, snapToEdge));
              };
              const classes = classNames(styles.position_absolute, {
                [styles.left]: !isRtl,
                [styles.right]: isRtl,
              });

              if (typeof this.props.left === 'boolean') {
                arrows.push(
                  <CSSTransition key="left" timeout={300} classNames={transitionNames}>
                    <div className={classes}>
                      <Shift x={-12 + leftShift.left} y={leftShift.top}>
                        <ScrollArrow direction="left" onClick={onClick} />
                      </Shift>
                    </div>
                  </CSSTransition>,
                );
              } else {
                arrows.push(
                  <div className={classes} key="left">
                    <Shift x={leftShift.left} y={leftShift.top}>
                      <div onClick={onClick}>{left}</div>
                    </Shift>
                  </div>,
                );
              }
            }
            if (right && enableRightArrow) {
              const onClick = () => {
                scroll(scrollArgs(0, 1, minDistance, focus, snapToEdge));
              };
              const classes = classNames(styles.position_absolute, {
                [styles.right]: !isRtl,
                [styles.left]: isRtl,
              });
              if (typeof this.props.right === 'boolean') {
                arrows.push(
                  <CSSTransition key="right" timeout={300} classNames={transitionNames}>
                    <div className={classes}>
                      <Shift x={12 + rightShift.left} y={rightShift.top}>
                        <ScrollArrow direction="right" onClick={onClick} />
                      </Shift>
                    </div>
                  </CSSTransition>,
                );
              } else {
                arrows.push(
                  <div className={`${styles.position_absolute} ${styles.right}`} key="right">
                    <Shift x={rightShift.left} y={rightShift.top}>
                      <div onClick={onClick}>{right}</div>
                    </Shift>
                  </div>,
                );
              }
            }
            if (top && enableTopArrow) {
              const onClick = () => {
                scroll(scrollArgs(-1, 0, minDistance, focus, snapToEdge));
              };
              const classes = classNames(styles.position_absolute, styles.top);
              if (typeof this.props.top === 'boolean') {
                arrows.push(
                  <CSSTransition key="top" timeout={300} classNames={transitionNames}>
                    <div className={classes} key="top">
                      <Shift x={topShift.left} y={-16 + topShift.top}>
                        <ScrollArrow direction="top" onClick={onClick} />
                      </Shift>
                    </div>
                  </CSSTransition>,
                );
              } else {
                arrows.push(
                  <div className={classes} key="top">
                    <Shift x={topShift.left} y={topShift.top}>
                      <div onClick={onClick}>{top}</div>
                    </Shift>
                  </div>,
                );
              }
            }
            if (bottom && enableBottomArrow) {
              const onClick = () => {
                scroll(scrollArgs(1, 0, minDistance, focus, snapToEdge));
              };
              const classes = classNames(styles.position_absolute, styles.bottom);
              if (typeof this.props.bottom === 'boolean') {
                arrows.push(
                  <CSSTransition key="bottom" timeout={300} classNames={transitionNames}>
                    <div className={classes}>
                      <Shift x={bottomShift.left} y={16 + bottomShift.top}>
                        <ScrollArrow direction="bottom" onClick={onClick} />
                      </Shift>
                    </div>
                  </CSSTransition>,
                );
              } else {
                arrows.push(
                  <div className={classes} key="bottom">
                    <Shift x={bottomShift.left} y={bottomShift.top}>
                      <div onClick={onClick}>{bottom}</div>
                    </Shift>
                  </div>,
                );
              }
            }
          }

          return (
            <React.Fragment>
              <TransitionGroup component={null}>{arrows}</TransitionGroup>
              <ForwardedBoundsValidator
                {...elements}
                ref={setContainer}
                container={container}
                onDirectionChange={this.onDirectionChange}
                isActive={isActive}
              >
                {children(setChild)}
              </ForwardedBoundsValidator>
            </React.Fragment>
          );
        }}
      </Scrollable>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  viewportCategory: state.meta.device.viewportCategory,
  isRtl: state.i18n.isRtl,
}))(ScrollAffordance);
